import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Pencil, Trash2, Mail, CheckCircle2, XCircle, FileText } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AsyncListShell } from "@/components/admin/AsyncListShell";
import { BuyerAssignment } from "@/components/admin/BuyerAssignment";
import { LeadPeriodControls, LeadPeriodCounter, TipoUnidadSelect, LEAD_FILTER_INITIAL } from "@/components/admin/LeadPeriodFilter";
import { formatIngreso, inPeriod, periodRange, tipoOf } from "@/lib/leadPeriod";

const FINANCIERAS = ["Banco Azteca", "CreditoGo", "Hey Banco", "RapidAuto", "AFIRME"];
const RESENDABLE = ["Registrado", "Precalificación completada", "Caso especial — revisión manual", "Lead abandonó precalificación", "Precalificado", "Caso especial"];
const APP_LABELS = {
  nombre_completo_ine: "Nombre (INE)", estado_civil: "Estado civil", rfc: "RFC", nacionalidad: "Nacionalidad",
  lugar_nacimiento: "Lugar de nacimiento", nivel_estudios: "Nivel de estudios", dependientes: "Dependientes",
  tipo_vivienda: "Tipo de vivienda", pago_vivienda_mensual: "Pago mensual vivienda", antiguedad_vivienda: "Antigüedad vivienda",
  actividad_economica: "Actividad / puesto", tipo_actividad: "Tipo de actividad", nombre_empresa: "Empresa",
  antiguedad_empleo: "Antigüedad empleo", direccion_empresa: "Dirección empresa", telefono_trabajo: "Teléfono trabajo",
  dado_alta_hacienda: "Alta en Hacienda", ingreso_mensual_promedio: "Ingreso mensual promedio",
  tipo_comprobante: "Comprobante", fecha_ingreso_empresa: "Fecha ingreso empresa",
};
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString("es-MX", { dateStyle: "short", timeStyle: "short" }) : null);

const ESTADOS = [
  "Registrado", "Precalificación completada", "Caso especial — revisión manual",
  "Lead nuevo", "Contactado", "Expediente en trámite", "Aprobado", "Rechazado", "Cerrado",
  "Lead abandonó precalificación", "Precalificado", "Caso especial", "Solicitud completa", "Autorizado",
];
const ESTADO_COLORS = {
  "Registrado": "bg-yellow-50 text-yellow-800 border border-yellow-200",
  "Precalificación completada": "bg-blue-50 text-blue-700 border border-blue-200",
  "Caso especial — revisión manual": "bg-orange-50 text-orange-800 border border-orange-300",
  "Lead nuevo": "bg-[#C4674F]/15 text-[#A0553F]",
  "Contactado": "bg-[#F1F3F3] text-[#3E5641]",
  "Expediente en trámite": "bg-[#3E5641] text-white",
  "Aprobado": "bg-[#A0553F] text-white",
  "Autorizado": "bg-green-100 text-green-800 border border-green-300",
  "Rechazado": "bg-red-50 text-red-700",
  "Cerrado": "bg-white border border-[#3E5641] text-[#3E5641]",
  "Lead abandonó precalificación": "bg-yellow-50 text-yellow-800 border border-yellow-200",
  "Precalificado": "bg-blue-50 text-blue-700 border border-blue-200",
  "Caso especial": "bg-orange-50 text-orange-800 border border-orange-300",
  "Solicitud completa": "bg-[#C4674F]/20 text-[#A0553F] border border-[#C4674F]/40",
};

export default function BuyersPage() {
  const [items, setItems] = useState([]);
  const [banks, setBanks] = useState([]);
  const [q, setQ] = useState("");
  const [estadoFilter, setEstadoFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [detail, setDetail] = useState(null);
  const [authorizing, setAuthorizing] = useState(null);
  const [rejecting, setRejecting] = useState(null);
  const [lf, setLf] = useState(LEAD_FILTER_INITIAL);
  const [agencies, setAgencies] = useState([]);
  const [advisors, setAdvisors] = useState([]);
  const [agencyFilter, setAgencyFilter] = useState("all");
  const [advisorFilter, setAdvisorFilter] = useState("all");
  const agencyName = (id) => agencies.find((a) => a.id === id)?.nombre;
  const advisorName = (id) => advisors.find((a) => a.id === id)?.nombre;

  function onAssigned(updated) {
    setDetail(updated);
    setItems((xs) => xs.map((x) => (x.id === updated.id ? updated : x)));
  }

  async function onTipoChange(b, tipo) {
    try {
      await api.patch(`/buyers/${b.id}`, { tipo_unidad: tipo });
      setDetail({ ...b, tipo_unidad: tipo });
      setItems((xs) => xs.map((x) => (x.id === b.id ? { ...x, tipo_unidad: tipo } : x)));
      toast.success(`Tipo de unidad: ${tipo}`);
    } catch (e) { toast.error(formatApiError(e)); }
  }

  async function onResendLink(b) {
    const prev = b.last_resume_link_sent_at ? `\n(Último envío: ${fmtDate(b.last_resume_link_sent_at)})` : "";
    if (!window.confirm(`¿Enviar a ${b.correo} el enlace para continuar donde se quedó?${prev}`)) return;
    try {
      await api.post(`/buyers/${b.id}/resend-continue-link`);
      toast.success(`Correo enviado a ${b.correo}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  async function onSendNextSteps(b) {
    if (!b.banco_asignado) { toast.error("Primero asigna una financiera (Editar)."); return; }
    if (!window.confirm(`¿Enviar a ${b.correo} los siguientes pasos de ${b.banco_asignado}?`)) return;
    try {
      await api.post(`/buyers/${b.id}/send-next-steps`, {});
      toast.success(`Pasos de ${b.banco_asignado} enviados a ${b.correo}`);
      setDetail(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  async function load() {
    setLoading(true);
    try {
      const [a, b, ag, ad] = await Promise.all([api.get("/buyers"), api.get("/partners/banks"), api.get("/partners/agencies"), api.get("/admin/advisors")]);
      setItems(a.data);
      setBanks(b.data);
      setAgencies(ag.data);
      setAdvisors(ad.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);

  const banksById = useMemo(() => {
    const m = {};
    for (const bank of banks) m[bank.id] = bank;
    return m;
  }, [banks]);

  const range = periodRange(lf.period, lf.desde, lf.hasta);
  const filtered = useMemo(() => items.filter((it) => {
    if (estadoFilter !== "all" && it.estado !== estadoFilter) return false;
    if (!inPeriod(it.created_at, range)) return false;
    if (lf.tipo !== "all" && tipoOf(it) !== lf.tipo) return false;
    if (agencyFilter === "none" ? !!it.agencia_id : agencyFilter !== "all" && it.agencia_id !== agencyFilter) return false;
    if (advisorFilter === "none" ? !!it.asesor_id : advisorFilter !== "all" && it.asesor_id !== advisorFilter) return false;
    if (!q.trim()) return true;
    const t = q.toLowerCase();
    return [it.nombre, it.telefono, it.correo, it.ciudad, it.folio_cliente]
      .filter(Boolean)
      .some((v) => v.toLowerCase().includes(t));
  }), [items, q, estadoFilter, lf, agencyFilter, advisorFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  async function onDelete(id) {
    if (!window.confirm("¿Eliminar comprador?")) return;
    try {
      await api.delete(`/buyers/${id}`);
      toast.success("Eliminado");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  const showRows = !loading && filtered.length > 0;

  return (
    <div data-testid="admin-buyers">
      <header className="mb-8">
        <p className="mono text-xs uppercase tracking-widest text-[#A0553F]">Módulo 03</p>
        <h1 className="mt-1 font-serif text-3xl text-[#3E5641] md:text-4xl">Compradores</h1>
        <p className="mt-1 text-sm text-[#6B7678]">Leads de crédito capturados desde el sitio o manualmente.</p>
      </header>

      <div className="mb-4 flex flex-wrap gap-3">
        <Input
          placeholder="Buscar por nombre, teléfono, correo o ciudad..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="max-w-sm"
          data-testid="buy-search"
        />
        <Select value={estadoFilter} onValueChange={setEstadoFilter}>
          <SelectTrigger className="w-56" data-testid="buy-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los estados</SelectItem>
            {ESTADOS.map((e) => (
              <SelectItem key={e} value={e}>{e}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <LeadPeriodControls value={lf} onChange={setLf} prefix="buy" />
        <Select value={agencyFilter} onValueChange={(v) => { setAgencyFilter(v); setAdvisorFilter("all"); }}>
          <SelectTrigger className="w-52" data-testid="buy-agency-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas las agencias</SelectItem>
            <SelectItem value="none">Sin agencia</SelectItem>
            {agencies.map((a) => <SelectItem key={a.id} value={a.id}>{a.nombre}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={advisorFilter} onValueChange={setAdvisorFilter}>
          <SelectTrigger className="w-52" data-testid="buy-advisor-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los asesores</SelectItem>
            <SelectItem value="none">Sin asesor</SelectItem>
            {advisors.filter((a) => agencyFilter === "all" || agencyFilter === "none" || a.agency_id === agencyFilter).map((a) => (
              <SelectItem key={a.id} value={a.id}>{a.nombre}{a.activo ? "" : " (inactivo)"}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {!loading && <LeadPeriodCounter rows={filtered} prefix="buy" />}

      <AsyncListShell
        loading={loading}
        isEmpty={!loading && filtered.length === 0}
        loadingTestId="buy-loading"
        emptyTestId="buy-empty"
        emptyLabel="Sin compradores aún."
      >
        <div className="overflow-hidden rounded-sm border border-[#3E5641]/10 bg-white">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F1F3F3] text-left text-xs uppercase tracking-wider text-[#6B7678]">
                <tr>
                  <th className="px-4 py-3">Ingreso</th>
                  <th className="px-4 py-3">Nombre</th>
                  <th className="px-4 py-3">Contacto</th>
                  <th className="px-4 py-3">Tipo</th>
                  <th className="px-4 py-3">Ciudad</th>
                  <th className="px-4 py-3">Auto en mente</th>
                  <th className="px-4 py-3">Estado</th>
                  <th className="px-4 py-3">Financiera asignada</th>
                  <th className="px-4 py-3">Agencia</th>
                  <th className="px-4 py-3">Asesor</th>
                  <th className="px-4 py-3">Aprobado</th>
                  <th className="px-4 py-3 text-right">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#3E5641]/10">
                {filtered.map((b) => (
                  <BuyerRow
                    key={b.id}
                    buyer={b}
                    bankName={b.banco_asignado ? (banksById[b.banco_asignado]?.nombre || b.banco_asignado) : null}
                    agencyName={agencyName(b.agencia_id)}
                    advisorName={advisorName(b.asesor_id)}
                    onDetail={() => setDetail(b)}
                    onEdit={() => setEditing(b)}
                    onDelete={() => onDelete(b.id)}
                    onResendLink={() => onResendLink(b)}
                    onAuthorize={() => setAuthorizing(b)}
                    onReject={() => setRejecting(b)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </AsyncListShell>

      {editing && (
        <BuyerDialog
          key={editing.id}
          item={editing}
          banks={banks}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
      {detail && <DetailDialog buyer={detail} onClose={() => setDetail(null)} onSendNextSteps={() => onSendNextSteps(detail)} onTipoChange={(t) => onTipoChange(detail, t)}
        assignment={<BuyerAssignment key={`${detail.id}-${detail.asesor_id}`} buyer={detail} agencies={agencies} advisors={advisors} onAssigned={onAssigned} />} />}
      {authorizing && <AuthorizeDialog buyer={authorizing} onClose={() => setAuthorizing(null)} onSaved={() => { setAuthorizing(null); load(); }} />}
      {rejecting && <RejectDialog buyer={rejecting} onClose={() => setRejecting(null)} onSaved={() => { setRejecting(null); load(); }} />}
    </div>
  );
}

function BuyerRow({ buyer: b, bankName, agencyName, advisorName, onDetail, onEdit, onDelete, onResendLink, onAuthorize, onReject }) {
  const canResend = RESENDABLE.includes(b.estado);
  const canAuthorizeOrReject = b.estado === "Solicitud completa";
  return (
    <tr className="hover:bg-[#F1F3F3]/50" data-testid={`buy-row-${b.id}`}>
      <td className="px-4 py-3 text-xs whitespace-nowrap text-[#6B7678]" data-testid={`buy-ingreso-${b.id}`}>{formatIngreso(b.created_at)}</td>
      <td className="px-4 py-3 font-medium">
        <button onClick={onDetail} className="text-left hover:underline" data-testid={`buy-name-${b.id}`}>{b.nombre}</button>
        {b.folio_cliente && <div className="mono text-[10px] text-[#6B7678]">{b.folio_cliente}</div>}
      </td>
      <td className="px-4 py-3">
        <div>{b.telefono}</div>
        <div className="text-xs text-[#6B7678]">{b.correo}</div>
      </td>
      <td className="px-4 py-3 text-xs whitespace-nowrap" data-testid={`buy-tipo-${b.id}`}>{tipoOf(b)}</td>
      <td className="px-4 py-3">{b.ciudad}</td>
      <td className="px-4 py-3 text-xs">{b.tiene_auto_en_mente ? "Sí" : "No"}</td>
      <td className="px-4 py-3">
        <span className={`inline-block rounded-sm px-2 py-1 text-xs ${ESTADO_COLORS[b.estado] || ""}`}>{b.estado}</span>
      </td>
      <td className="px-4 py-3 text-xs" data-testid={`buy-financiera-${b.id}`}>{bankName || "—"}</td>
      <td className="px-4 py-3 text-xs" data-testid={`buy-agencia-${b.id}`}>{agencyName || "—"}</td>
      <td className="px-4 py-3 text-xs" data-testid={`buy-asesor-${b.id}`}>{advisorName || "—"}</td>
      <td className="px-4 py-3 mono">{b.monto_aprobado ? `$${Number(b.monto_aprobado).toLocaleString()}` : "—"}</td>
      <td className="px-4 py-3 text-right whitespace-nowrap">
        <button onClick={onDetail} className="mr-1 p-1 hover:bg-[#F1F3F3]" title="Ver detalle" data-testid={`buy-detail-${b.id}`}><FileText size={14} /></button>
        {canResend && (
          <button onClick={onResendLink} className="mr-1 p-1 hover:bg-[#F1F3F3] text-[#A0553F]"
            title={b.last_resume_link_sent_at ? `Enviar enlace para continuar · último envío ${fmtDate(b.last_resume_link_sent_at)}` : "Enviar enlace para continuar"}
            data-testid={`buy-resend-${b.id}`}><Mail size={14} /></button>
        )}
        {canAuthorizeOrReject && (
          <>
            <button onClick={onAuthorize} className="mr-1 p-1 hover:bg-green-50 text-green-700" title="Autorizar" data-testid={`buy-authorize-${b.id}`}><CheckCircle2 size={14} /></button>
            <button onClick={onReject} className="mr-1 p-1 hover:bg-red-50 text-red-600" title="Rechazar" data-testid={`buy-reject-${b.id}`}><XCircle size={14} /></button>
          </>
        )}
        <button onClick={onEdit} className="mr-1 p-1 hover:bg-[#F1F3F3]" title="Editar" data-testid={`buy-edit-${b.id}`}><Pencil size={14} /></button>
        <button onClick={onDelete} className="p-1 text-red-600 hover:bg-red-50" title="Eliminar" data-testid={`buy-delete-${b.id}`}><Trash2 size={14} /></button>
      </td>
    </tr>
  );
}

function DetailDialog({ buyer: b, onClose, onSendNextSteps, onTipoChange, assignment }) {
  const aem = b.auto_en_mente || {};
  const pre = b.prequalification || {};
  const mr = b.match_result || {};
  const app = b.application || {};
  const auth = b.authorization || {};
  const rej = b.rejection || {};
  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">{b.nombre}</DialogTitle>
          <p className="text-xs text-[#6B7678] mono">{b.id}</p>
        </DialogHeader>
        <div className="grid gap-4 md:grid-cols-2">
          <div className="text-sm"><span className="text-[#6B7678]">Fecha y hora de ingreso:</span> <span className="font-medium text-[#3E5641]" data-testid="detail-ingreso">{formatIngreso(b.created_at)}</span></div>
          <div><Label className="field-label">Tipo de unidad</Label><TipoUnidadSelect value={tipoOf(b)} onChange={onTipoChange} testId="detail-tipo-unidad" /></div>
        </div>
        {assignment}
        <Block title="Contacto">
          <KV k="Teléfono" v={b.telefono} /><KV k="Correo" v={b.correo} /><KV k="Ciudad" v={b.ciudad} />
          <KV k="Estado" v={b.estado} /><KV k="Folio" v={b.folio_cliente} />
          <KV k="Financiera asignada" v={b.banco_asignado} />
          <KV k="Último enlace para continuar" v={fmtDate(b.last_resume_link_sent_at)} />
          <KV k="Pasos de financiera enviados" v={fmtDate(b.next_steps_sent_at)} />
        </Block>
        {b.tiene_auto_en_mente && Object.keys(aem).length > 0 && (
          <Block title="Auto en mente">{Object.entries(aem).map(([k, v]) => <KV key={k} k={k} v={v} />)}</Block>
        )}
        {Object.keys(pre).length > 0 && (
          <Block title="Precalificación">
            <KV k="Edad" v={pre.edad} /><KV k="Tipo de ingreso" v={pre.tipo_ingreso} />
            <KV k="Ingreso mensual" v={pre.ingreso_mensual ? `$${Number(pre.ingreso_mensual).toLocaleString()}` : "—"} />
            <KV k="Debe a Azteca" v={pre.debe_a_azteca} /><KV k="Buró" v={pre.buro} />
            <KV k="Valor del auto" v={`$${Number(pre.valor_auto).toLocaleString()}`} />
            <KV k="Enganche %" v={`${pre.enganche_pct}%`} />
            <KV k="Intentos crédito 3m" v={pre.intentos_credito_3m} />
          </Block>
        )}
        {mr.candidates && (
          <Block title="Match sugerido">
            <KV k="Candidatos" v={(mr.candidates || []).join(", ") || "Ninguno"} />
            {mr.hey_banco_requires_aval && <KV k="Hey Banco" v="Requiere aval" />}
            {mr.show_buro_unknown_notice && <KV k="Leyenda buró" v="Se aplicó (no conoce buró)" />}
            {mr.afirme_needs_manual_review_reason && <KV k="⚠ Revisión manual" v={mr.afirme_needs_manual_review_reason} />}
          </Block>
        )}
        {Object.keys(app).length > 0 && (
          <Block title="Solicitud completa">
            {Object.entries(app).filter(([k]) => k !== "referencias" && k !== "banco_elegido").map(([k, v]) => <KV key={k} k={APP_LABELS[k] || k} v={typeof v === "boolean" ? (v ? "Sí" : "No") : v} />)}
            {app.referencias?.map((r, i) => (
              <div key={i} className="mt-2 rounded-sm bg-[#F1F3F3] p-2 text-xs">
                <p className="mono uppercase text-[#A0553F]">Ref {i + 1}</p>
                <div>{r.nombre} · {r.telefono} · {r.parentesco}</div>
                <div className="text-[#6B7678]">{r.direccion}</div>
              </div>
            ))}
          </Block>
        )}
        {Object.keys(auth).length > 0 && (
          <Block title="Autorización">{Object.entries(auth).map(([k, v]) => <KV key={k} k={k} v={v} />)}</Block>
        )}
        {Object.keys(rej).length > 0 && (
          <Block title="Rechazo">{Object.entries(rej).map(([k, v]) => <KV key={k} k={k} v={v} />)}</Block>
        )}
        <DialogFooter className="gap-2">
          {b.application && FINANCIERAS.includes(b.banco_asignado) && (
            <button onClick={onSendNextSteps} className="btn-primary" data-testid="detail-send-next-steps">
              <Mail size={14} className="mr-2" /> Enviar pasos de {b.banco_asignado}
            </button>
          )}
          <button onClick={onClose} className="btn-outline-dark" data-testid="detail-close">Cerrar</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BankSelect({ value, onChange, testId }) {
  const opts = value && !FINANCIERAS.includes(value) ? [value, ...FINANCIERAS] : FINANCIERAS;
  return (
    <Select value={value || undefined} onValueChange={onChange}>
      <SelectTrigger data-testid={testId}><SelectValue placeholder="Selecciona financiera" /></SelectTrigger>
      <SelectContent>{opts.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
    </Select>
  );
}

function Block({ title, children }) {
  return <div className="mt-4 border-t border-[#3E5641]/10 pt-4"><p className="mono text-[10px] uppercase tracking-widest text-[#A0553F] mb-2">{title}</p><div className="grid gap-2 md:grid-cols-2">{children}</div></div>;
}
function KV({ k, v }) {
  return <div className="text-sm"><span className="text-[#6B7678]">{k}:</span> <span className="text-[#3E5641] font-medium">{v == null || v === "" ? "—" : String(v)}</span></div>;
}

function AuthorizeDialog({ buyer, onClose, onSaved }) {
  const banco0 = buyer.banco_asignado || buyer.application?.banco_elegido || "";
  const [f, setF] = useState({
    banco_autoriza: banco0, monto_financiar: "", pago_inicial: "", mensualidad: "",
    plazo_meses: "48", anio_auto: "", precio_auto: "", sugerencias: "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  async function save() {
    const empty = Object.entries(f).find(([, v]) => String(v).trim() === "");
    if (empty) { toast.error("Completa todos los campos antes de autorizar."); return; }
    setSaving(true);
    try {
      await api.patch(`/buyers/${buyer.id}/authorize`, {
        banco_autoriza: f.banco_autoriza,
        monto_financiar: Number(f.monto_financiar), pago_inicial: Number(f.pago_inicial),
        mensualidad: Number(f.mensualidad), plazo_meses: Number(f.plazo_meses),
        anio_auto: Number(f.anio_auto), precio_auto: Number(f.precio_auto),
        sugerencias: f.sugerencias.trim(),
      });
      toast.success("Autorización guardada. Correo de felicitación enviado al cliente.");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  }
  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" aria-describedby={undefined}>
        <DialogHeader><DialogTitle className="font-serif text-2xl">Autorizar — {buyer.nombre}</DialogTitle></DialogHeader>
        <div className="grid gap-4 md:grid-cols-2">
          <div><Label className="field-label">Banco que autoriza</Label><BankSelect value={f.banco_autoriza} onChange={(v) => set("banco_autoriza", v)} testId="auth-banco" /></div>
          <div><Label className="field-label">Monto a financiar (MXN)</Label><Input type="number" value={f.monto_financiar} onChange={(e) => set("monto_financiar", e.target.value)} data-testid="auth-monto" /></div>
          <div><Label className="field-label">Pago inicial total (MXN)</Label><Input type="number" value={f.pago_inicial} onChange={(e) => set("pago_inicial", e.target.value)} data-testid="auth-inicial" /><p className="field-hint">Incluye enganche, seguro, localizador, etc.</p></div>
          <div><Label className="field-label">Mensualidad (MXN)</Label><Input type="number" value={f.mensualidad} onChange={(e) => set("mensualidad", e.target.value)} data-testid="auth-mensualidad" /></div>
          <div><Label className="field-label">Plazo (meses)</Label><Input type="number" value={f.plazo_meses} onChange={(e) => set("plazo_meses", e.target.value)} data-testid="auth-plazo" /></div>
          <div><Label className="field-label">Año del auto</Label><Input type="number" value={f.anio_auto} onChange={(e) => set("anio_auto", e.target.value)} data-testid="auth-anio" /></div>
          <div className="md:col-span-2"><Label className="field-label">Precio del auto (MXN)</Label><Input type="number" value={f.precio_auto} onChange={(e) => set("precio_auto", e.target.value)} data-testid="auth-precio" /></div>
          <div className="md:col-span-2"><Label className="field-label">Sugerencias profesionales para el cliente</Label><Textarea rows={4} value={f.sugerencias} onChange={(e) => set("sugerencias", e.target.value)} data-testid="auth-sugerencias" /></div>
        </div>
        <DialogFooter>
          <button onClick={onClose} className="btn-outline-dark">Cancelar</button>
          <button onClick={save} disabled={saving} className="btn-primary disabled:opacity-60" data-testid="auth-save">{saving ? "Guardando..." : "Autorizar y notificar"}</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RejectDialog({ buyer, onClose, onSaved }) {
  const banco0 = buyer.banco_asignado || buyer.application?.banco_elegido || "";
  const [f, setF] = useState({ banco: banco0, motivo: "" });
  const [saving, setSaving] = useState(false);
  async function save() {
    if (!f.banco) { toast.error("Selecciona la financiera que rechazó."); return; }
    setSaving(true);
    try {
      await api.patch(`/buyers/${buyer.id}/reject`, { banco: f.banco, motivo: f.motivo.trim() || null });
      toast.success("Rechazo enviado. Correo con opciones de rescate mandado al cliente.");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  }
  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" aria-describedby={undefined}>
        <DialogHeader><DialogTitle className="font-serif text-2xl">Rechazar — {buyer.nombre}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div><Label className="field-label">Banco que rechazó</Label><BankSelect value={f.banco} onChange={(v) => setF({ ...f, banco: v })} testId="rej-banco" /></div>
          <div><Label className="field-label">Motivo (opcional, se incluye en el correo al cliente)</Label><Textarea rows={3} value={f.motivo} onChange={(e) => setF({ ...f, motivo: e.target.value })} data-testid="rej-motivo" /></div>
          <p className="text-xs text-[#6B7678]">El correo al cliente incluye 3 alternativas de rescate: aplicar con otra financiera, obligado solidario, o ajustar monto.</p>
        </div>
        <DialogFooter>
          <button onClick={onClose} className="btn-outline-dark">Cancelar</button>
          <button onClick={save} disabled={saving} className="btn-primary disabled:opacity-60" data-testid="rej-save">{saving ? "Enviando..." : "Rechazar y notificar"}</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BuyerDialog({ item, banks, onClose, onSaved }) {
  const [form, setForm] = useState({
    nombre: item.nombre,
    telefono: item.telefono,
    correo: item.correo,
    ciudad: item.ciudad,
    tiene_auto_en_mente: item.tiene_auto_en_mente ? "si" : "no",
    estado: item.estado,
    banco_asignado: item.banco_asignado || "none",
    monto_aprobado: item.monto_aprobado ?? "",
    notas: item.notas || "",
  });
  const [saving, setSaving] = useState(false);
  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  async function save() {
    setSaving(true);
    try {
      const payload = {
        nombre: form.nombre.trim(),
        telefono: form.telefono.trim(),
        correo: form.correo.trim(),
        ciudad: form.ciudad.trim(),
        tiene_auto_en_mente: form.tiene_auto_en_mente === "si",
        estado: form.estado,
        banco_asignado: form.banco_asignado === "none" ? null : form.banco_asignado,
        monto_aprobado: form.monto_aprobado === "" ? null : Number(form.monto_aprobado),
        notas: form.notas?.trim() || null,
      };
      await api.patch(`/buyers/${item.id}`, payload);
      toast.success("Comprador actualizado");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">{form.nombre}</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 md:grid-cols-2">
          <F label="Nombre"><Input value={form.nombre} onChange={(e) => setF("nombre", e.target.value)} /></F>
          <F label="Teléfono"><Input value={form.telefono} onChange={(e) => setF("telefono", e.target.value)} /></F>
          <F label="Correo"><Input type="email" value={form.correo} onChange={(e) => setF("correo", e.target.value)} /></F>
          <F label="Ciudad"><Input value={form.ciudad} onChange={(e) => setF("ciudad", e.target.value)} /></F>
          <div>
            <Label className="field-label">Auto en mente</Label>
            <Select value={form.tiene_auto_en_mente} onValueChange={(v) => setF("tiene_auto_en_mente", v)}>
              <SelectTrigger data-testid="buy-mente"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="si">Sí</SelectItem>
                <SelectItem value="no">No</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="field-label">Estado</Label>
            <Select value={form.estado} onValueChange={(v) => setF("estado", v)}>
              <SelectTrigger data-testid="buy-estado"><SelectValue /></SelectTrigger>
              <SelectContent>
                {ESTADOS.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="field-label">Banco / financiera asignada</Label>
            <Select value={form.banco_asignado} onValueChange={(v) => setF("banco_asignado", v)}>
              <SelectTrigger data-testid="buy-banco"><SelectValue placeholder="Sin asignar" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— Sin asignar —</SelectItem>
                {FINANCIERAS.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}
                {banks.filter((b) => !FINANCIERAS.includes(b.nombre)).map((b) => <SelectItem key={b.id} value={b.id}>{b.nombre}</SelectItem>)}
                {form.banco_asignado !== "none" && !FINANCIERAS.includes(form.banco_asignado) && !banks.some((b) => b.id === form.banco_asignado) && (
                  <SelectItem value={form.banco_asignado}>{form.banco_asignado}</SelectItem>
                )}
              </SelectContent>
            </Select>
          </div>
          <F label="Monto aprobado ($)"><Input type="number" value={form.monto_aprobado} onChange={(e) => setF("monto_aprobado", e.target.value)} data-testid="buy-monto" /></F>
        </div>
        <div>
          <Label className="field-label">Notas</Label>
          <Textarea rows={3} value={form.notas} onChange={(e) => setF("notas", e.target.value)} data-testid="buy-notas" />
        </div>
        <DialogFooter>
          <button onClick={onClose} className="btn-outline-dark">Cancelar</button>
          <button onClick={save} disabled={saving} className="btn-primary disabled:opacity-60" data-testid="buy-save">
            {saving ? "Guardando..." : "Guardar"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function F({ label, children }) {
  return <div><Label className="field-label">{label}</Label>{children}</div>;
}
